import { Grid, Paper } from "@material-ui/core";
import { activityItemTypeTags } from "features/activity/activityConsts";
import { useGetAllActivityItemsQuery, useGetAllActivityTypesQuery, useGetWashActivitiesQuery } from "features/activity/activitySlice";
import { useGetClientsQuery } from "features/clients/clientSlice";
import { useGetRulesByActivityItemQuery } from "features/clients/contracts/MechanicalWorkConfig/mechanicalsSlice";
import { useGetLotsQuery } from "features/lots/lotSlice";
import { selectTenantName } from "features/user/userSlice";
import { useTitle } from "hooks/useTitle";
import moment from "moment";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import FormatService from "services/formatService";
import FilterService from "../../../../services/filterService";
import { dateFilterTypes } from "../../../loads/components/LoadsDashboard/loadsDashboardConsts";
import ActivityListTable from "../MechanicalDashboard/ActivityList/ActivityListTable";
import AssetsWashedByLot from "./AssetsWashedByLot/AssetsWashedByLot";
import AssetsWashedTable from "./AssetsWashedTable/AssetsWashedTable";
import ComplianceByLotWidget from "./ComplianceByLotWidget/ComplianceByLotWidget";
import { ActivityExceptionsWidget } from "./ExceptionsWidget";
import { getFilterConfigs, getWashFilterConfigs } from './filterConfigs';
import DashboardFilters from "./Filters/DashboardFilters";
import WashFilters from "./Filters/WashFilters";
import { NewWashDashboardStyle } from "./NewWashDashboardStyle";
import TotalAssetsWidget from "./TotalAssetsWidget/TotalAssetsWidget";
import ImportActivities from "features/activity/ImportActivities/ImportActivities";
import Authorize from "components/Authorize";
import { permissionProfiles } from "components/Authorize/permissionProfiles";

const NewWashDashboardPage = () => {
    const classes = NewWashDashboardStyle();

    useTitle('New Wash Dashboard');
    const { activityFilters, locationFilters } = useSelector(state => state.washDashboardState);
    const tenantName = useSelector(selectTenantName);

    let startDate;
    if (activityFilters?.['startDate']?.value) {
        const sd = new Date(activityFilters['startDate'].value);
        if (!isNaN(sd.getTime())) {
            sd.setHours(0, 0, 0, 0);
            startDate = sd.toISOString();
        }
    }

    let endDate;
    if (activityFilters?.['endDate']?.value) {
        const ed = new Date(activityFilters['endDate'].value);
        if (!isNaN(ed.getTime())) {
            ed.setHours(23, 59, 59, 999);
            endDate = ed.toISOString();
        }
    }

    let { data: activityItems, error: activityItemsError, isFetching: isLoadingActivityItems } = useGetAllActivityItemsQuery();
    let { data: activityTypes, error: activityTypesError, isFetching: isLoadingActivityTypes } = useGetAllActivityTypesQuery();

    const cleaningType = activityTypes?.find(a => a.name == 'Cleaning');
    const basicWash = activityItems?.find(item => item.typeTag == activityItemTypeTags.BasicWash);
    activityItems = activityItems?.filter(item => item.activityTypeId == cleaningType?.id);

    let { data: rulesByActivityItem, error: rulesByActivityItemError, isLoading: isLoadingRulesByActivityItem } = useGetRulesByActivityItemQuery(basicWash?.id, { skip: !basicWash });
    let { data: washActivities, error: washActivitiesError, isFetching: isLoadingWashActivities, refetch } = useGetWashActivitiesQuery({ startDate: startDate, endDate: endDate });
    let { data: lots, error: lotsError, isLoading: isLoadingLots } = useGetLotsQuery();
    let { data: clients, error: clientsError, isLoading: isLoadingClients } = useGetClientsQuery();

    let allActivities = washActivities?.allActivities || [];

    clients = clients || [];
    rulesByActivityItem = rulesByActivityItem || [];
    lots = lots || [];

    const lotsLookup = useMemo(() =>
        lots?.reduce((acc, lot) => { acc[lot.id] = lot; return acc; }, {}),
        [lots]
    );

    const lotsWithWashRule = useMemo(() =>
        rulesByActivityItem?.map(rule => ({ lotId: rule.lotId, name: lotsLookup[rule.lotId]?.name }))?.filter(rule => rule.lotId && rule.name),
        [rulesByActivityItem, lotsLookup]
    );

    const mappedLots = lots.map(lot => ({
        ...lot,
        lotFilter: [
            ...(lotsLookup[lot.id]?.ownedByTenant ? ['tenant'] : []),
            ...(lotsWithWashRule.some(l => l.lotId === lot.id) ? ['washRule'] : [])
        ],
    }));

    const isByDay = activityFilters['dateFilterType']?.value < dateFilterTypes.QUARTER;
    const format = isByDay ? "YYYY-MM-DD" : "YYYY-MM";

    const mappedCompleteWashes = useMemo(() => {
        return washActivities?.completedActivities?.map(wash => ({
            ...wash,
            lotName: lotsLookup[wash.lotId]?.name || "",
            tenantLot: lotsLookup[wash.lotId]?.ownedByTenant || false,
            washDate: FormatService.formatDateNoConversion(wash.activityDateFormatted),
            autoGenerated: wash.systemGenerated ? 'true' : 'false',
            lotFilter: [
                ...(lotsLookup[wash.lotId]?.ownedByTenant ? ['tenant'] : []),
                ...(lotsWithWashRule.some(l => l.lotId === wash.lotId) ? ['washRule'] : [])
            ],
            groupKey: moment(wash.activityDate).format(format),
            month: moment(wash.activityDate).format("YYYY-MM"),
            day: moment(wash.activityDate).format("YYYY-MM-DD"),
        })) || [];
    }, [washActivities, lotsLookup, lotsWithWashRule, format]);

    const mappedExceptionWashes = useMemo(() => {
        return washActivities?.exceptionActivities?.map(wash => ({
            ...wash,
            lotName: lotsLookup[wash.lotId]?.name || "",
            tenantLot: lotsLookup[wash.lotId]?.ownedByTenant || false,
            washDate: FormatService.formatDateNoConversion(wash.activityDateFormatted),
            autoGenerated: wash.systemGenerated ? 'true' : 'false',
            lotFilter: [
                ...(lotsLookup[wash.lotId]?.ownedByTenant ? ['tenant'] : []),
                ...(lotsWithWashRule.some(l => l.lotId === wash.lotId) ? ['washRule'] : [])
            ],
            groupKey: moment(wash.activityDate).format(format),
            month: moment(wash.activityDate).format("YYYY-MM"),
            day: moment(wash.activityDate).format("YYYY-MM-DD"),
        })) || [];
    }, [washActivities, lotsLookup, lotsWithWashRule, format]);

    const mainFilterConfigs = getFilterConfigs({ lots, clients, tenantName });

    const washFilterConfigs = getWashFilterConfigs({
        activityItems: activityItems?.filter(item => item.activityTypeId === cleaningType?.id)
    });

    const lotFilters = { ...locationFilters };
    const washFilters = { ...activityFilters };
    if (washFilters.autoGenerated?.value === 'all') {
        washFilters.autoGenerated = { value: null };
    }
    if (lotFilters.lotFilter?.value === 'all') {
        lotFilters.lotFilter = { value: null };
    }

    const filteredCompleteWashes = FilterService.filter({ ...lotFilters, ...washFilters }, null, mappedCompleteWashes, [...mainFilterConfigs, ...washFilterConfigs]);
    const filteredExceptionWashes = FilterService.filter({ ...lotFilters, ...washFilters }, null, mappedExceptionWashes, [...mainFilterConfigs, ...washFilterConfigs]);
    const mainFilteredLots = FilterService.filter(lotFilters, null, mappedLots, mainFilterConfigs);
    const activityFilteredLots = FilterService.filter(washFilters, null, mainFilteredLots, washFilterConfigs);

    const isLoading = isLoadingWashActivities || isLoadingRulesByActivityItem || isLoadingLots || isLoadingActivityItems;

    return (
        <Grid container spacing={2}>
            <Authorize profile={permissionProfiles.DASHBOARD.MECHANICAL_DASHBOARD_IMPORT_ACTIVITIES}>
                <Grid item xs={12} className={classes.filterItem} container justifyContent="flex-end">
                    <ImportActivities refetch={refetch} />
                </Grid>
            </Authorize>
            <Grid item xs={12}>
                <Paper className={classes.slaWidgetsWrapper}>
                    <DashboardFilters refetch={refetch} filterConfigs={mainFilterConfigs} filteredLots={mainFilteredLots} />
                </Paper>
            </Grid>

            <Grid container spacing={2} className={classes.washWidgetsBorder}>
                <Grid item xs={12}>
                    <WashFilters activityItems={activityItems} filterConfigs={washFilterConfigs} />
                </Grid>

                <Grid item xs={12} md={4}>
                    <Paper className={classes.widgetWrapper}>
                        <TotalAssetsWidget
                            washes={filteredCompleteWashes}
                            isLoading={isLoading}
                        />
                    </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Paper className={classes.widgetWrapper}>
                        <AssetsWashedByLot
                            washes={filteredCompleteWashes}
                            isLoading={isLoading}
                        />
                    </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Paper className={classes.widgetWrapper}>
                        <ActivityExceptionsWidget
                            activities={filteredExceptionWashes}
                            lots={activityFilteredLots}
                            isLoading={isLoading}
                        />
                    </Paper>
                </Grid>
            </Grid>

            <Grid item xs={12} md={6}>
                <Paper className={classes.slaWidgetsWrapper}>
                    <AssetsWashedTable
                        lotsWithWashRule={lotsWithWashRule}
                        lots={mainFilteredLots}
                        isLoadingData={isLoading}
                        filterConfig={mainFilterConfigs}
                    />
                </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
                <Paper className={classes.slaWidgetsWrapper}>
                    <ComplianceByLotWidget
                        lotsWithWashRule={lotsWithWashRule}
                        lots={mainFilteredLots}
                        filterConfigs={mainFilterConfigs}
                    />
                </Paper>
            </Grid>

            <Grid item xs={12}>
                <ActivityListTable
                    activities={allActivities}
                    infoTextOnTable='Use the Filters to the left to narrow the search results in this table. The filters at the top of the page do not impact the "Activities" table'
                    title='Washes'
                    showWorkRequest
                />
            </Grid>

        </Grid>
    )
}

export default NewWashDashboardPage;
